// InvoiceList — loads full invoice history from GET /invoices when autoFetch is enabled.
import React, { useEffect, useState } from 'react';

export const getInvoiceApiBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location) {
    const port = window.location.port;
    const hostname = window.location.hostname;
    if (hostname === 'localhost' && (port === '3000' || port === '3001')) {
      return 'http://localhost:8001/api/v1';
    }
    return '/api/v1';
  }
  return 'http://localhost:8001/api/v1';
};

const InvoiceList = ({
  invoices: invoicesProp,
  onPayInvoice,
  autoFetch = false,
  showPayButton = false,
  refreshKey = 0,
  apiBaseUrl: apiBaseUrlProp,
}) => {
  const [invoices, setInvoices] = useState(invoicesProp || []);
  const [loading, setLoading] = useState({});
  const [fetchLoading, setFetchLoading] = useState(autoFetch);
  const [fetchError, setFetchError] = useState(null);

  const API_BASE_URL = apiBaseUrlProp || getInvoiceApiBaseUrl();

  useEffect(() => {
    if (!autoFetch) {
      setInvoices(invoicesProp || []);
      return undefined;
    }

    let cancelled = false;
    const loadInvoices = async () => {
      setFetchLoading(true);
      setFetchError(null);
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
        const response = await fetch(`${API_BASE_URL}/invoices?limit=500`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('Session expired. Please log in again.');
          }
          throw new Error('Failed to load invoices');
        }

        const data = await response.json();
        if (!cancelled) {
          setInvoices(Array.isArray(data) ? data : data.data || []);
        }
      } catch (error) {
        if (!cancelled) {
          setFetchError(error.message || 'Failed to load invoices');
          setInvoices([]);
        }
      } finally {
        if (!cancelled) {
          setFetchLoading(false);
        }
      }
    };

    loadInvoices();
    return () => {
      cancelled = true;
    };
  }, [autoFetch, refreshKey, API_BASE_URL]);

  useEffect(() => {
    if (!autoFetch && invoicesProp) {
      setInvoices(invoicesProp);
    }
  }, [invoicesProp, autoFetch]);

  const handlePayInvoice = (invoiceId) => {
    if (onPayInvoice) {
      onPayInvoice(invoiceId);
    }
  };

  const handleDownloadPDF = async (invoiceId) => {
    try {
      setLoading((prev) => ({ ...prev, [invoiceId]: true }));
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/pdf`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          alert('Your session has expired. Please log in again.');
          window.location.href = '/login';
          return;
        }
        throw new Error('Failed to download PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${invoiceId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setLoading((prev) => ({ ...prev, [invoiceId]: false }));
    }
  };

  const handleViewHTML = async (invoiceId) => {
    try {
      setLoading((prev) => ({ ...prev, [`html_${invoiceId}`]: true }));
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/html`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          alert('Your session has expired. Please log in again.');
          window.location.href = '/login';
          return;
        }
        throw new Error('Failed to load HTML invoice');
      }

      const htmlContent = await response.text();
      const newWindow = window.open('', '_blank');
      newWindow.document.write(htmlContent);
      newWindow.document.close();
    } catch (error) {
      console.error('Error viewing HTML invoice:', error);
      alert('Failed to view HTML invoice. Please try again.');
    } finally {
      setLoading((prev) => ({ ...prev, [`html_${invoiceId}`]: false }));
    }
  };

  if (fetchLoading) {
    return (
      <div className="text-center py-8 text-gray-500 text-sm">Loading invoices...</div>
    );
  }

  if (fetchError) {
    return (
      <div className="text-center py-8 text-red-600 text-sm">{fetchError}</div>
    );
  }

  if (!invoices || invoices.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No invoices found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">{invoices.length} invoice{invoices.length !== 1 ? 's' : ''}</p>
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {invoices.map((invoice) => (
            <li key={invoice.id} className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      Invoice #{invoice.id.slice(0, 8).toUpperCase()}
                    </p>
                    <div className="ml-2 flex-shrink-0 flex">
                      <p
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          invoice.status === 'paid'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {invoice.status}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 flex justify-between">
                    <div className="sm:flex">
                      <p className="flex items-center text-sm text-gray-500">
                        Amount: ${invoice.total_amount}
                      </p>
                      <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                        Due: {new Date(invoice.due_date).toLocaleDateString()}
                      </p>
                      {invoice.paid_at && (
                        <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                          Paid: {new Date(invoice.paid_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="ml-6 flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => handleDownloadPDF(invoice.id)}
                    disabled={loading[invoice.id]}
                    className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
                  >
                    {loading[invoice.id] ? 'Downloading...' : 'Download PDF'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleViewHTML(invoice.id)}
                    disabled={loading[`html_${invoice.id}`]}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
                  >
                    {loading[`html_${invoice.id}`] ? 'Loading...' : 'View HTML'}
                  </button>
                  {showPayButton && invoice.status !== 'paid' && onPayInvoice && (
                    <button
                      type="button"
                      onClick={() => handlePayInvoice(invoice.id)}
                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
                    >
                      Pay
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default InvoiceList;
